import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { maintenanceApi, companiesApi, usersApi, ticketsApi } from "../services/api";
import { useFilterMine, useTechnicianLookup } from "../context/FilterMineContext";
import { useAuth } from "../context/AuthContext";
import { attachmentCount, ticketHasAttachments } from "../utils/attachments";
import { useNotification } from "../context/NotificationContext";
import { parseUtc } from "../utils/utcDate";
import { isDueDatePast } from "../utils/dueDate";
import { ticketStatusLabel } from "../components/ticketStatusVocabulary";

const MAINT_CAT_BADGE = {
  network:  "bg-blue-100 text-blue-700",
  hardware: "bg-gray-100 text-gray-700",
  software: "bg-purple-100 text-purple-700",
  security: "bg-red-100 text-red-700",
  backup:   "bg-yellow-100 text-yellow-700",
  other:    "bg-gray-100 text-gray-500",
};
const TICKET_CAT_BADGE = {
  backups:        { label: "גיבויים",     cls: "bg-blue-100 text-blue-700" },
  m365:           { label: "M365",        cls: "bg-purple-100 text-purple-700" },
  av_edr:         { label: "AV/EDR",      cls: "bg-red-100 text-red-700" },
  updates:        { label: "עדכונים",     cls: "bg-orange-100 text-orange-700" },
  firewall:       { label: "חומת אש",     cls: "bg-pink-100 text-pink-700" },
  email_security: { label: "אבטחת מייל",  cls: "bg-indigo-100 text-indigo-700" },
  reports:        { label: "דוחות",       cls: "bg-teal-100 text-teal-700" },
};
const MAINT_CAT_HE = { network: "רשת", hardware: "חומרה", software: "תוכנה", security: "אבטחה", backup: "גיבוי", other: "אחר" };
// Categories chosen in TicketDetail's "move to maintenance" dialog are stored on the new item too.
const QUEUE_CAT_HE = { backups: "גיבויים", m365: "M365", av_edr: "AV/EDR", updates: "עדכונים ותיקונים", firewall: "חומת אש", email_security: "אבטחת מייל", reports: "דוחות" };

// unassigned: update_maintenance auto-reverts status here when a technician is cleared from an
// in_progress item (maintenance.py) -- omitted here left the badge unstyled and the row's Hebrew
// label falling back to the raw English string, same class of drift already fixed for tickets.
// waiting and closed are real, reachable values too (maintenance.py's own MAINT_DONE/"waiting"
// stat checks, mobile/src/utils/labels.ts's MAINTENANCE_STATUS_LABELS already covers all seven).
const MAINT_STATUS_BADGE = {
  unassigned:  "bg-gray-100 text-gray-600",
  open:        "bg-blue-100 text-blue-700",
  in_progress: "bg-yellow-100 text-yellow-700",
  waiting:     "bg-purple-100 text-purple-700",
  done:        "bg-green-100 text-green-700",
  closed:      "bg-gray-100 text-gray-500",
  cancelled:   "bg-gray-100 text-gray-400",
};
const TICKET_STATUS_BADGE = {
  open:        "bg-blue-100 text-blue-700",
  in_progress: "bg-yellow-100 text-yellow-700",
  pending:     "bg-purple-100 text-purple-700",
  closed:      "bg-gray-100 text-gray-500",
};
const MAINT_STATUS_HE  = { unassigned: "לא הוקצה", open: "פתוח", in_progress: "בטיפול", waiting: "ממתין", done: "הושלם", closed: "סגור", cancelled: "בוטל" };
const PRIORITY_HE = { low: "נמוכה", medium: "בינונית", high: "גבוהה", critical: "קריטית" };

const asUtc = (iso) => /Z|[+-]\d\d:\d\d$/.test(iso) ? iso : iso + "Z";
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(asUtc(iso));
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
};
const isPast = isDueDatePast;

const MAINT_FINISHED = new Set(["done", "cancelled", "closed"]);   // the backend's MAINT_DONE: a cancelled item is never overdue
const EMPTY_FORM = { title: "", category: "other", company_id: "", assigned_to: "", priority: "medium", status: "open", due_date: "", recurring: false };

export default function MaintenancePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { showOnlyMine } = useFilterMine();
  const { user } = useAuth();
  const { technicianId: myTechnicianId, isError: myTechnicianLookupFailed } = useTechnicianLookup();
  const { addNotification } = useNotification();

  const assignedToFilter = showOnlyMine && user?.id ? { assigned_to: user.id } : {};
  const techFilter = showOnlyMine && myTechnicianId ? { technician_id: myTechnicianId } : {};
  // "Only mine" with the technician id not known yet (list loading / failed): the request went out WITHOUT the filter and the page
  // listed everybody's queue tickets under a "mine only" label. Hold the query instead (same as TicketsPage / Dashboard).
  const minePending = showOnlyMine && !myTechnicianId;

  const { data: items = [], isLoading, isError: itemsFailed } = useQuery({
    queryKey: ["maintenance", assignedToFilter],
    queryFn: () => maintenanceApi.list({ limit: 500, ...assignedToFilter }),
  });
  const { data: linkedTickets = [], isLoading: isLoadingTickets, isError: linkedFailed } = useQuery({
    queryKey: ["tickets-maintenance-queue", techFilter],
    queryFn: () => ticketsApi.list({ queue_type: "maintenance", limit: 500, ...techFilter }),
    enabled: !minePending,
  });
  const { data: companies = [] } = useQuery({ queryKey: ["companies"], queryFn: () => companiesApi.list() });
  const { data: users = [] }     = useQuery({ queryKey: ["users"],     queryFn: () => usersApi.list() });

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const createItem = useMutation({
    mutationFn: (data) => maintenanceApi.create(data),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["maintenance"] });
      setShowForm(false);
      setForm(EMPTY_FORM);
      navigate(`/maintenance/${created.id}`);
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה ביצירת משימת תחזוקה", "error"),
  });

  const rows = Array.isArray(items) ? items : (items.items ?? []);

  const merged = [
    ...rows.map((m) => ({
      _type: "maintenance", id: m.id,
      title: m.title, company: m.company,
      person: m.assignee?.display_name || "—",
      catLabel: MAINT_CAT_HE[m.category] || QUEUE_CAT_HE[m.category] || m.category || "—",
      catCls: MAINT_CAT_BADGE[m.category] || "bg-gray-100 text-gray-500",
      statusBadge: MAINT_STATUS_BADGE[m.status] || "bg-gray-100 text-gray-600",
      statusLabel: MAINT_STATUS_HE[m.status] || m.status,
      date: m.updated_at || m.due_date,
      due_date: m.due_date, status: m.status,
      recurring: m.recurring,
    })),
    ...linkedTickets.map((t) => {
      const cat = TICKET_CAT_BADGE[t.ai_category];
      return {
        _type: "ticket", id: t.id,
        title: t.title, company: t.company,
        person: t.sender_name,
        catLabel: cat ? cat.label : ({ Network: "רשת", Hardware: "חומרה", Software: "תוכנה", Email: "דואר", Security: "אבטחה", Other: "אחר" }[t.ai_category] || t.ai_category || "—"),
        catCls: cat ? cat.cls : "bg-gray-100 text-gray-500",
        statusBadge: TICKET_STATUS_BADGE[t.status] || "bg-gray-100 text-gray-500",
        statusLabel: ticketStatusLabel(t.status),
        date: t.updated_at,
        due_date: null, status: t.status,
        ticket_number: t.ticket_number, has_reply: t.has_customer_reply,
        attachments_count: t.attachments_count,
        has_attachments: t.has_attachments,
        hasAttachments: t.hasAttachments,
      };
    }),
  ].sort((a, b) => parseUtc(b.date) - parseUtc(a.date));

  return (
    <div className="space-y-4" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">🔧 תחזוקה</h1>
          {!isLoading && !isLoadingTickets && (
            <span className="text-sm text-gray-400">{merged.length} פריטים</span>
          )}
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="bg-green-600 hover:bg-green-700 text-white text-sm px-4 py-2 rounded-lg transition-colors"
        >
          + משימת תחזוקה חדשה
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <div className="bg-white rounded-xl border p-5 space-y-4">
          <h3 className="font-semibold text-gray-800">משימת תחזוקה חדשה</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs text-gray-500 mb-1">כותרת *</label>
              <input
                autoFocus
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="שם המשימה"
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">קטגוריה</label>
              <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                {Object.entries(MAINT_CAT_HE).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">חברה</label>
              <select value={form.company_id} onChange={(e) => setForm((f) => ({ ...f, company_id: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value="">—</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">אחראי</label>
              <select value={form.assigned_to} onChange={(e) => setForm((f) => ({ ...f, assigned_to: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value="">—</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.display_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">עדיפות</label>
              <select value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                {Object.entries(PRIORITY_HE).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">תאריך יעד</label>
              <input type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <input
                type="checkbox"
                id="recurring"
                checked={form.recurring}
                onChange={(e) => setForm((f) => ({ ...f, recurring: e.target.checked }))}
                className="accent-green-600"
              />
              <label htmlFor="recurring" className="text-sm text-gray-700 cursor-pointer">תחזוקה חוזרת</label>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }} className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50">ביטול</button>
            <button
              onClick={() => {
                if (!form.title.trim()) return;
                createItem.mutate({
                  title: form.title.trim(),
                  category: form.category,
                  company_id: form.company_id || null,
                  assigned_to: form.assigned_to || null,
                  priority: form.priority,
                  status: form.status,
                  due_date: form.due_date || null,
                  recurring: form.recurring,
                });
              }}
              disabled={!form.title.trim() || createItem.isPending}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50"
            >
              {createItem.isPending ? "יוצר..." : "צור משימה"}
            </button>
          </div>
        </div>
      )}

      {rows.length >= 500 && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          מוצגות 500 משימות התחזוקה האחרונות בלבד — ייתכן שמשימות ישנות יותר (כולל חוזרות) אינן מופיעות.
        </p>
      )}
      {/* Unified table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        {(isLoading || isLoadingTickets) ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
          </div>
        ) : merged.length === 0 && (itemsFailed || linkedFailed) ? (
          // a failed request is not "no maintenance items"
          <div role="alert" className="text-center py-16 text-red-600">
            <p className="font-medium">לא ניתן לטעון את משימות התחזוקה</p>
          </div>
        ) : merged.length === 0 && showOnlyMine && myTechnicianLookupFailed ? (
          // a failed technician-identity lookup used to look the same as "no tasks assigned to me"
          <div role="alert" className="text-center py-16 text-red-600">
            <p className="font-medium">לא ניתן לאמת את זהות הטכנאי — לא ניתן להציג "רק שלי" כרגע</p>
          </div>
        ) : merged.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <p className="text-4xl mb-3">🔧</p>
            <p>אין משימות תחזוקה עדיין</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50 text-gray-600 text-xs">
                <th className="text-right px-4 py-3 font-medium">#</th>
                <th className="text-right px-4 py-3 font-medium">כותרת</th>
                <th className="text-right px-4 py-3 font-medium">חברה</th>
                <th className="text-right px-4 py-3 font-medium">קטגוריה</th>
                <th className="text-right px-4 py-3 font-medium">אחראי / שולח</th>
                <th className="text-right px-4 py-3 font-medium">סטטוס</th>
                <th className="text-right px-4 py-3 font-medium">סוג</th>
                <th className="text-right px-4 py-3 font-medium">עדכון / יעד</th>
              </tr>
            </thead>
            <tbody>
              {merged.map((row, idx) => (
                <tr
                  key={`${row._type}-${row.id}`}
                  onClick={() => navigate(row._type === "maintenance" ? `/maintenance/${row.id}` : `/tickets/${row.id}`)}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter" && e.key !== " ") return;
                    e.preventDefault();
                    navigate(row._type === "maintenance" ? `/maintenance/${row.id}` : `/tickets/${row.id}`);
                  }}
                  tabIndex={0}
                  role="button"
                  className="border-b last:border-0 hover:bg-green-50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 text-gray-400 text-xs font-mono">
                    <div className="inline-flex items-center gap-1 whitespace-nowrap">
                      <span>{row._type === "ticket" ? `#${row.ticket_number}` : idx + 1}</span>
                      {row._type === "ticket" && ticketHasAttachments(row) && (
                        <span title={`${attachmentCount(row)} קבצים מצורפים`} className="inline-flex items-center gap-0.5 text-slate-400 text-sm leading-none flex-shrink-0">
                          📎
                          <span className="text-[10px] font-semibold leading-none">{attachmentCount(row)}</span>
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900">
                    <div className="flex items-center gap-1.5">
                      {row.title}
                      {row.has_reply && <span className="text-orange-500 text-sm leading-none">✉</span>}
                      {row.recurring && <span className="text-xs text-green-600">🔄</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{row.company?.name || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${row.catCls}`}>
                      {row.catLabel}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{row.person}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${row.statusBadge}`}>
                      {row.statusLabel}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${row._type === "maintenance" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                      {row._type === "maintenance" ? "תחזוקה" : "קריאה"}
                    </span>
                  </td>
                  <td className={`px-4 py-3 text-xs whitespace-nowrap ${row.due_date && isPast(row.due_date) && !MAINT_FINISHED.has(row.status) ? "text-red-600 font-medium" : "text-gray-400"}`}>
                    {fmtDate(row.due_date || row.date)}
                    {row.due_date && isPast(row.due_date) && !MAINT_FINISHED.has(row.status) && " ⚠️"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
